import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  ApolloProvider as OriginalApolloProvider,
} from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { setContext } from '@apollo/link-context';
import { onError } from '@apollo/link-error';
import * as Sentry from '@sentry/browser';
import { CachePersistor } from 'apollo-cache-persist';
import Loader from 'Components/Forms/Loader';
import useConfig from 'hooks/useConfig';
import { useFirebase } from 'FeatureModules/Auth/Contexts/FirebaseContext';
import resolvers from 'graphql/secure/resolvers';
import typeDefs from 'graphql/secure/typePolicies/typeDefs';
import { typePolicies, possibleTypes } from 'graphql/secure/typePolicies';
import omitDeep from 'omit-deep-lodash';
import React, { FC, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { GET_INITIAL_STATE, GET_RESET_STATE } from './queries';
import typeSerializers from './typePolicies/typeSerializers';
import { transformVariableTypes } from '../../utils/apollo.utils';
import { v4 as uuid } from 'uuid';

const cache = new InMemoryCache({
  possibleTypes,
  typePolicies,
});

const initialState = {
  drawerOpen: true,
};

const resetState = {
  settingsSidebarOpen: false,
};

export const secureCachePersistor = new CachePersistor({
  key: 'secure-apollo-cache',
  cache,
  debounce: 200,
  // @ts-ignore Check later again why this is not typed correctly
  storage: window.localStorage,
});

const SecureApolloProvider: FC = () => {
  const config = useConfig();
  const { firebase } = useFirebase();

  const initialize = async () => {
    // write initial state so local queries dont error out
    cache.writeQuery({
      query: GET_INITIAL_STATE,
      data: initialState,
    });

    // restore state from localStorage
    await secureCachePersistor.restore();

    // reset state so e.g. create modal does not open
    cache.writeQuery({
      query: GET_RESET_STATE,
      data: resetState,
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  if (!process.env.REACT_APP_GQL_SECURE_ENDPOINT) {
    throw new Error('REACT_APP_GQL_SECURE_ENDPOINT is not defined');
  }

  const httpLink = createHttpLink({ uri: process.env.REACT_APP_GQL_SECURE_ENDPOINT });

  const authLink = setContext(async (_, { headers }) => {
    let customHeaders = {};

    try {
      const token = await firebase.getFirebase().auth().currentUser?.getIdToken();

      customHeaders = {
        authorization: token ? `Bearer ${token}` : '',
        'X-Request-ID': uuid(),
      };
    } catch (e) {}

    return {
      headers: {
        ...headers,
        ...customHeaders,
      },
    };
  });

  // remove '__typename' property for mutations since input types do
  // not equal types in schema to avoid breaking mutations
  const omitTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = omitDeep(operation.variables, '__typename');
    }

    return forward(operation);
  });

  const middlewareLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = transformVariableTypes(operation.variables, typeSerializers);
    }

    return forward(operation);
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) => {
        const error = `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`;
        Sentry.captureMessage(error);
        return console.error(error);
      });
    }

    if (networkError) console.error(`[Network error]: ${networkError}`);
  });

  // @ts-ignore
  const link = from([errorLink, middlewareLink, omitTypenameLink, authLink.concat(httpLink)]);

  const apolloClient = new ApolloClient({
    cache,
    resolvers,
    link,
    typeDefs,
    connectToDevTools: process.env.NODE_ENV === 'development',
    defaultOptions: {
      // @ts-ignore This does not makes any sense here but it is still used somewhere
      firebase,
      config,
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
      },
    },
  });

  if (process.env.NODE_ENV === 'development') {
    // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
  }

  if (!apolloClient) return <Loader />;

  return (
    <OriginalApolloProvider client={apolloClient}>
      <Outlet />
    </OriginalApolloProvider>
  );
};

export default SecureApolloProvider;
